import React, { useEffect, useMemo, useState } from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import Styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";

import twoFaIcon from "../../../theme/assets/svg/b_cabinet_profile_2fa_icon.svg";
import TwoFactorCodeInput from "../../_UI/twoFactorCodeInput";

import signInIcon from "../../../theme/assets/svg/b_cabinet_profile_2fa_signin_icon.svg";
import passwordIcon from "../../../theme/assets/svg/b_cabinet_profile_2fa_password_icon.svg";
import transactionIcon from "../../../theme/assets/svg/b_cabinet_profile_2fa_transaction_icon.svg";
import depositIcon from "../../../theme/assets/svg/b_cabinet_deposit_icon.svg";

const CODE_LENGTH = 6;

const scopeIcons = {
    login: signInIcon,
    password_change: passwordIcon,
    transaction: transactionIcon,
    deposit: depositIcon
};

const getScopeIcon = (scopeValue) => {
    return scopeIcons[scopeValue] || null;
};

const getScopeConfig = (scope) => {
    switch (scope?.value) {
        case "login":
            return {
                title: "Вход в аккаунт",
                description: "Запрашивать 2FA-код при входе в аккаунт.",
            };
        case "password_change":
            return {
                title: "Изменение пароля",
                description: "Запрашивать 2FA-код при изменении пароля.",
            };
        case "transaction":
            return {
                title: "Отправка транзакций",
                description: "Запрашивать 2FA-код для каждого исходящего криптоперевода.",
            };
        default:
            return {
                title: scope?.label || scope?.value || "2FA",
                description: "Запрашивать 2FA-код для этого действия.",
            };
    }
};

const arraysEqual = (firstArray, secondArray) => {
    if (firstArray.length !== secondArray.length) return false;

    const firstSorted = [...firstArray].sort();
    const secondSorted = [...secondArray].sort();

    return firstSorted.every((item, index) => item === secondSorted[index]);
};

const CabinetProfileTwoFactorSettingsModal = ({
                                                  open,
                                                  onClose,
                                                  onSave,
                                                  twoFactorData,
                                                  isLoading = false,
                                                  formErr = null,
                                              }) => {
    const { t } = useTranslation();

    const availableScopes = useMemo(() => {
        return Array.isArray(twoFactorData?.available_scopes)
            ? twoFactorData.available_scopes
            : [];
    }, [twoFactorData]);

    const initialScopes = useMemo(() => {
        return Array.isArray(twoFactorData?.scopes)
            ? twoFactorData.scopes
            : [];
    }, [twoFactorData]);

    const [selectedScopes, setSelectedScopes] = useState(initialScopes);
    const [verificationCode, setVerificationCode] = useState("");

    const isChanged = !arraysEqual(selectedScopes, initialScopes);

    const isSaveDisabled =
        isLoading ||
        !isChanged ||
        verificationCode.length !== CODE_LENGTH;

    const closeHandler = () => {
        if (isLoading) return;

        if (onClose) {
            onClose();
        }
    };

    const toggleScopeHandler = (scopeValue) => {
        if (isLoading) return;

        setSelectedScopes((prevState) => {
            if (prevState.includes(scopeValue)) {
                return prevState.filter((item) => item !== scopeValue);
            }

            return [...prevState, scopeValue];
        });
    };

    const saveHandler = () => {
        if (isSaveDisabled) return;

        const payload = {
            scopes: selectedScopes,
            code: verificationCode,
        };

        if (onSave) {
            onSave(payload);
        }
    };

    useEffect(() => {
        if (open) {
            setSelectedScopes(initialScopes);
            setVerificationCode("");
        }
    }, [open, initialScopes]);

    return (
        <Popup
            open={!!open}
            modal
            nested
            closeOnDocumentClick={!isLoading}
            onClose={closeHandler}
            className="cabinetProfileTwoFactorSettingsModal"
        >
            <div className={Styles.modalContainer}>
                <div
                    className={`${Styles.modalCloseBtn} ${isLoading ? Styles.modalCloseBtnDisabled : ""}`}
                    onClick={closeHandler}
                >
                    <div />
                    <div />
                </div>

                <div className={Styles.modalContentWrap}>
                    <div className={Styles.titleRow}>
                        <div className={Styles.titleIcon}>
                            <img src={twoFaIcon} alt="2fa" />
                        </div>

                        <div className={Styles.titleTextWrap}>
                            <div className={Styles.title}>
                                {t("Настройки 2FA")}
                            </div>

                            <div className={Styles.note}>
                                {t("Измените настройки двухфакторной аутентификации")}
                            </div>
                        </div>
                    </div>

                    <div className={Styles.scopesList}>
                        {availableScopes.map((scope) => {
                            const scopeConfig = getScopeConfig(scope);
                            const scopeIcon = getScopeIcon(scope.value);
                            const isActive = selectedScopes.includes(scope.value);

                            return (
                                <div
                                    className={Styles.scopeItem}
                                    key={scope.value}
                                >
                                    <div className={Styles.scopeIcon}>
                                        {scopeIcon && (
                                            <img
                                                src={scopeIcon}
                                                alt={t(scopeConfig.title)}
                                            />
                                        )}
                                    </div>

                                    <div className={Styles.scopeText}>
                                        <div className={Styles.scopeTitle}>
                                            {t(scopeConfig.title)}
                                        </div>

                                        <div className={Styles.scopeDescription}>
                                            {t(scopeConfig.description)}
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        className={`
                                            ${Styles.scopeToggle}
                                            ${isActive ? Styles.scopeToggleActive : ""}
                                        `}
                                        onClick={() => toggleScopeHandler(scope.value)}
                                        disabled={isLoading}
                                        aria-label={t(scopeConfig.title)}
                                    >
                                        <span />
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    <TwoFactorCodeInput
                        value={verificationCode}
                        onChange={setVerificationCode}
                        formErr={formErr}
                        errorFieldName="code"
                        isLoading={isLoading}
                        label="Код аутентификации"
                        hint="Введите код, который сейчас отображается в приложении."
                    />

                    <div className={Styles.infoNotice}>
                        <div className={Styles.infoIcon}>i</div>

                        <div className={Styles.infoText}>
                            {t("Эти настройки помогают добавить дополнительную защиту для важных действий в аккаунте.")}
                        </div>
                    </div>

                    <div className={Styles.actionsRow}>
                        <button
                            type="button"
                            className={Styles.cancelBtn}
                            onClick={closeHandler}
                            disabled={isLoading}
                        >
                            {t("Отмена")}
                        </button>

                        <button
                            type="button"
                            className={Styles.saveBtn}
                            onClick={saveHandler}
                            disabled={isSaveDisabled}
                        >
                            {isLoading ? t("Сохранение") : t("Сохранить изменения")}
                        </button>
                    </div>
                </div>
            </div>
        </Popup>
    );
};

export default CabinetProfileTwoFactorSettingsModal;